import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, type Assignment, type User } from "../api";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { UpstreamSettingsPanel } from "../components/UpstreamSettingsPanel";
import { copyText, generatePassword } from "../lib/utils";

type UserWithAssignments = User & { assignments: Assignment[] };

export function AdminPage() {
  const { user } = useAuth();
  const { push } = useToast();
  const [users, setUsers] = useState<UserWithAssignments[]>([]);
  const [resourceKeys, setResourceKeys] = useState<{ resourceKey: string; hits: number }[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  // Create user (one form — password auto-filled)
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState(() => generatePassword());
  const [newRole, setNewRole] = useState<"user" | "admin">("user");
  const [createdCreds, setCreatedCreds] = useState<{ username: string; password: string } | null>(
    null
  );

  // Inline assign
  const [assignDraft, setAssignDraft] = useState("");

  const load = useCallback(async () => {
    const [usersData, keysData] = await Promise.all([
      api<{ users: UserWithAssignments[] }>("/api/admin/users"),
      api<{ keys: { resourceKey: string; hits: number }[] }>("/api/admin/resource-keys"),
    ]);
    setUsers(usersData.users);
    setResourceKeys(keysData.keys);
    setSelectedId((prev) => {
      if (prev && usersData.users.some((u) => u.id === prev)) return prev;
      return usersData.users[0]?.id ?? null;
    });
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    void load().catch((err) =>
      push(err instanceof Error ? err.message : "Failed to load users", "error")
    );
  }, [user, load, push]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        u.role.includes(q) ||
        u.assignments.some((a) => a.resourceKey.toLowerCase().includes(q))
    );
  }, [users, query]);

  const selected = users.find((u) => u.id === selectedId) ?? null;

  const suggestedKeys = useMemo(() => {
    if (!selected) return [];
    const have = new Set(selected.assignments.map((a) => a.resourceKey));
    return resourceKeys.filter((k) => !have.has(k.resourceKey)).slice(0, 12);
  }, [selected, resourceKeys]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  async function createUser(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const created = await api<{ user: User }>("/api/admin/users", {
        method: "POST",
        body: JSON.stringify({
          username: newUsername.trim(),
          password: newPassword,
          role: newRole,
        }),
      });
      setCreatedCreds({ username: created.user.username, password: newPassword });
      setNewUsername("");
      setNewPassword(generatePassword());
      setNewRole("user");
      push(`Created ${created.user.username}`, "ok");
      await load();
      setSelectedId(created.user.id);
    } catch (err) {
      push(err instanceof Error ? err.message : "Create failed", "error");
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(u: User) {
    setBusy(true);
    try {
      await api(`/api/admin/users/${u.id}`, {
        method: "PATCH",
        body: JSON.stringify({ active: !u.active }),
      });
      push(u.active ? `${u.username} disabled` : `${u.username} enabled`, "ok");
      await load();
    } catch (err) {
      push(err instanceof Error ? err.message : "Update failed", "error");
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword(u: User) {
    const password = generatePassword();
    setBusy(true);
    try {
      await api(`/api/admin/users/${u.id}`, {
        method: "PATCH",
        body: JSON.stringify({ password }),
      });
      setCreatedCreds({ username: u.username, password });
      push(`Password reset for ${u.username}`, "ok");
    } catch (err) {
      push(err instanceof Error ? err.message : "Reset failed", "error");
    } finally {
      setBusy(false);
    }
  }

  async function addAssignment(resourceKey: string) {
    if (!selected || !resourceKey.trim()) return;
    setBusy(true);
    try {
      await api("/api/admin/assignments", {
        method: "POST",
        body: JSON.stringify({
          userId: selected.id,
          resourceKey: resourceKey.trim(),
        }),
      });
      setAssignDraft("");
      push(`Assigned ${resourceKey.trim()}`, "ok");
      await load();
    } catch (err) {
      push(err instanceof Error ? err.message : "Assign failed", "error");
    } finally {
      setBusy(false);
    }
  }

  async function removeAssignment(assignmentId: string, key: string) {
    setBusy(true);
    try {
      await api(`/api/admin/assignments/${assignmentId}`, { method: "DELETE" });
      push(`Removed ${key}`, "ok");
      await load();
    } catch (err) {
      push(err instanceof Error ? err.message : "Remove failed", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page page-wide">
      <header className="page-header">
        <div>
          <h1>Users</h1>
          <p className="muted">Create accounts and assign which resources each person can see.</p>
        </div>
      </header>

      <UpstreamSettingsPanel />

      {createdCreds && (
        <div className="cred-banner">
          <div>
            <strong>Share these credentials once</strong>
            <p className="mono">
              {createdCreds.username} / {createdCreds.password}
            </p>
          </div>
          <div className="row-actions">
            <button
              type="button"
              className="btn ghost compact"
              onClick={() =>
                void copyText(`${createdCreds.username}\n${createdCreds.password}`).then((ok) =>
                  push(ok ? "Copied" : "Copy failed", ok ? "ok" : "error")
                )
              }
            >
              Copy
            </button>
            <button type="button" className="btn ghost compact" onClick={() => setCreatedCreds(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="admin-layout">
        <section className="surface list-pane">
          <div className="toolbar">
            <input
              className="search"
              placeholder="Search users…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <form className="create-inline" onSubmit={createUser}>
            <input
              placeholder="New username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              required
              minLength={3}
              disabled={busy}
            />
            <div className="password-row">
              <input
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={12}
                disabled={busy}
                aria-label="Password"
              />
              <button
                type="button"
                className="btn ghost compact"
                disabled={busy}
                onClick={() => setNewPassword(generatePassword())}
              >
                Regenerate
              </button>
            </div>
            <div className="create-row">
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as "user" | "admin")}
                disabled={busy}
              >
                <option value="user">Standard</option>
                <option value="admin">Admin</option>
              </select>
              <button type="submit" disabled={busy || !newUsername.trim()}>
                Add user
              </button>
            </div>
          </form>

          <ul className="user-list">
            {filtered.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  className={u.id === selectedId ? "user-row active" : "user-row"}
                  onClick={() => setSelectedId(u.id)}
                >
                  <span className="user-row-main">
                    <span className="user-name">{u.username}</span>
                    <span className="muted tiny">
                      {u.role}
                      {!u.active ? " · disabled" : ""}
                      {" · "}
                      {u.assignments.length} assigned
                    </span>
                  </span>
                  <span className={`dot ${u.active ? "on" : "off"}`} />
                </button>
              </li>
            ))}
            {filtered.length === 0 && <li className="muted empty-inline">No users match.</li>}
          </ul>
        </section>

        <section className="surface detail-pane">
          {!selected ? (
            <p className="muted empty-inline">Select a user to manage assignments.</p>
          ) : (
            <>
              <div className="detail-head">
                <div>
                  <h2>{selected.username}</h2>
                  <p className="muted">
                    {selected.role === "admin"
                      ? "Admins see every resource automatically."
                      : "Only assigned resources appear in their live feed."}
                  </p>
                </div>
                <div className="row-actions">
                  <button
                    type="button"
                    className="btn ghost compact"
                    disabled={busy}
                    onClick={() => void resetPassword(selected)}
                  >
                    Reset password
                  </button>
                  <button
                    type="button"
                    className="btn ghost compact"
                    disabled={busy || selected.id === user?.id}
                    onClick={() => void toggleActive(selected)}
                  >
                    {selected.active ? "Disable" : "Enable"}
                  </button>
                </div>
              </div>

              {selected.role !== "admin" && (
                <>
                  <form
                    className="assign-bar"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void addAssignment(assignDraft);
                    }}
                  >
                    <input
                      list="resource-key-suggestions"
                      placeholder="Type a resource key and press Enter"
                      value={assignDraft}
                      onChange={(e) => setAssignDraft(e.target.value)}
                      disabled={busy}
                    />
                    <datalist id="resource-key-suggestions">
                      {resourceKeys.map((k) => (
                        <option key={k.resourceKey} value={k.resourceKey} />
                      ))}
                    </datalist>
                    <button type="submit" disabled={busy || !assignDraft.trim()}>
                      Assign
                    </button>
                  </form>

                  {suggestedKeys.length > 0 && (
                    <div className="suggest-row">
                      <span className="muted tiny">Quick add</span>
                      <div className="chip-row">
                        {suggestedKeys.map((k) => (
                          <button
                            key={k.resourceKey}
                            type="button"
                            className="chip action"
                            disabled={busy}
                            onClick={() => void addAssignment(k.resourceKey)}
                          >
                            {k.resourceKey}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="chip-row assigned">
                    {selected.assignments.length === 0 && (
                      <p className="muted">No resources assigned yet — they won’t see any events.</p>
                    )}
                    {selected.assignments.map((a) => (
                      <span key={a.id} className="chip">
                        {a.resourceKey}
                        <button
                          type="button"
                          className="chip-x"
                          aria-label={`Remove ${a.resourceKey}`}
                          disabled={busy}
                          onClick={() => void removeAssignment(a.id, a.resourceKey)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </>
              )}

              {selected.role === "admin" && (
                <div className="info-callout">
                  No per-resource assignment needed. This account already receives the full feed.
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
